import React from 'react';
import { GameInterface } from '../../types/GameInterface';
import SubtextProgressTracker from './SubtextProgressTracker.tsx';
import SubtextWordsFoundBank from './SubtextWordsFoundBank.tsx';
import SubtextCurrentWord from './SubtextCurrentWord.tsx';
import SubtextWordBlocks from './SubtextWordBlocks.tsx';
import SubtextControls from './SubtextControls.tsx';
import SubtextWordsModal from './SubtextWordsModal.tsx';
import SubtextRankingsModal from './SubtextRankingsModal.tsx';

import { getCurrentRank, RANK_LEVELS, getTotalPointsForWord, BonusType } from './progressLevels.ts';
import { setSubtextGameStats, getGameNumberStats, SubtextGameStats } from '../../utils/userStatsUtils.ts';
import { GameType } from '../../types/GameType.ts';
import { 
  logWordEntered, 
  logInvalidWordAttempt, 
  logReachedNewRank, 
  logFoundAllWords,
  logClickProgressTracker,
  logClickWordsFoundBank,
  logClickRandomize
} from '../../utils/loggingUtils.ts';
import './subtext.css';

interface SubtextGameProps extends GameInterface {
  dailyWord: string;
  realisticMaxScore: number;
  longestSubWordLength: number;
  validSubWords: string[];
}

interface Toast {
  id: number;
  message: string;
  points: string;
  bonusType?: BonusType;
  timestamp: number;
}

interface GameState {
  currentWord: string;
  foundWords: string[];
  score: number;
  /** Tracks which actual character positions from the daily word have been used to build the current word */
  usedPositions: Set<number>;
  /** Tracks the display order of characters (maps display index to actual character position) */
  displayOrder: number[];
  foundAllWords: boolean;
  // Custom toast state
  successToasts: Toast[];
  // Custom error toast state
  errorMessage: string;
  showErrorToast: boolean;
  isShaking: boolean;
  // Modal state
  isWordsModalOpen: boolean;
  isRankingsModalOpen: boolean;
  // Randomization state
  isRandomized: boolean;
  /** Tracks which shuffle is next: 1 = reverse, 2 = interleave, 3 = alphabetical */
  nextShuffleState: number;
}

class SubtextGame extends React.Component<SubtextGameProps, GameState> {
  keyboardListener: (event: KeyboardEvent) => void;
  errorTimeout: NodeJS.Timeout | null = null;

  constructor(props: SubtextGameProps) {
    super(props);
    this.state = {
      currentWord: '',
      foundWords: [],
      score: 0,
      usedPositions: new Set(),
      // Initialize display order to show characters in their original order (display index = actual position)
      displayOrder: Array.from({ length: props.dailyWord.length }, (_, i) => i),
      foundAllWords: false,
      successToasts: [],
      errorMessage: '',
      showErrorToast: false,
      isShaking: false,
      isWordsModalOpen: false,
      isRankingsModalOpen: false,
      isRandomized: false,
      nextShuffleState: 1,
    };
    this.keyboardListener = this.processKeyboardEvent.bind(this);
  }

  // Get success message based on word length/score and bonus type
  getSuccessMessage(wordLength: number, bonusType?: BonusType): string {
    // Special messages for bonus words
    if (bonusType === 'anagram') return 'Anagram!';
    if (bonusType === 'longest_word') return 'Max Length!';
    
    // Regular messages based on word length
    if (wordLength >= 7) return 'Amazing!';
    if (wordLength >= 6) return 'Great!';
    if (wordLength >= 5) return 'Good!';
    if (wordLength >= 4) return 'Nice!';
    return 'Good!';
  }

  // Show custom success toast
  showCustomSuccessToast(message: string, points: number, bonusType?: BonusType): void {
    const newToast: Toast = {
      id: Date.now(),
      message: message,
      points: `+${points}`,
      bonusType: bonusType,
      timestamp: Date.now(),
    };

    this.setState(prevState => ({
      successToasts: [...prevState.successToasts, newToast],
    }));

    // Auto-hide after animation completes
    setTimeout(() => {
      this.setState(prevState => ({
        successToasts: prevState.successToasts.filter(toast => toast.id !== newToast.id),
      }));
    }, bonusType ? 4000 : 2000); // 4 seconds for bonus toasts, 2 seconds for regular toasts
  }

  // Show custom error toast with shake animation
  showCustomErrorToast(message: string): void {
    // Clear any existing timeout
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }

    this.setState({
      errorMessage: message,
      showErrorToast: true,
      isShaking: true,
    });

    // Auto-hide error toast after 2 seconds
    setTimeout(() => {
      this.setState({
        showErrorToast: false,
      });
    }, 2000);

    // Remove word and stop shaking after 1 second
    this.errorTimeout = setTimeout(() => {
      this.setState({
        currentWord: '',
        usedPositions: new Set(),
        isShaking: false,
      });
      this.errorTimeout = null;
    }, 1000);
  }

  // Cancel error animation (when user starts typing during shake)
  cancelErrorAnimation(): void {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
    this.setState({
      isShaking: false,
      showErrorToast: false,
    });
  }

  // Modal handlers
  openWordsModal(): void {
    logClickWordsFoundBank(GameType.SUBTEXT, this.props.gameNumber);
    this.setState({ isWordsModalOpen: true });
  }

  closeWordsModal(): void {
    this.setState({ isWordsModalOpen: false });
  }

  openRankingsModal(): void {
    logClickProgressTracker(GameType.SUBTEXT, this.props.gameNumber);
    this.setState({ isRankingsModalOpen: true });
  }

  closeRankingsModal(): void {
    this.setState({ isRankingsModalOpen: false });
  }

  // Add character to current word by clicking a specific actual position
  addCharacterByPosition(char: string, actualPosition: number): void {
    if (this.state.foundAllWords) {
      return;
    }

    // Cancel error animation if user starts typing during shake
    if (this.state.isShaking) {
      this.cancelErrorAnimation();
    }

    if (this.state.usedPositions.has(actualPosition)) {
      this.showCustomErrorToast(`This ${char.toLowerCase()} is already used!`);
      return;
    }
    
    // Use functional state update to ensure we're working with the latest state
    this.setState(prevState => {
      const newUsedPositions = new Set(prevState.usedPositions);
      newUsedPositions.add(actualPosition);
      
      return {
        currentWord: prevState.currentWord + char.toLowerCase(),
        usedPositions: newUsedPositions,
      };
    });
  }

  // Add character to current word by typing (find first available actual position)
  addCharacterByTyping(char: string): void {
    if (this.state.foundAllWords) {
      return;
    }

    // Cancel error animation if user starts typing during shake
    if (this.state.isShaking) {
      this.cancelErrorAnimation();
    }

    const lowerChar = char.toLowerCase();
    
    // Find first available actual position of this character
    const availableActualPosition = this.props.dailyWord.split('').findIndex((c, index) => 
      c.toLowerCase() === lowerChar && !this.state.usedPositions.has(index)
    );
    
    if (availableActualPosition === -1) {
      return;
    }
    
    this.addCharacterByPosition(char, availableActualPosition);
  }

  // Remove last character
  removeCharacter(): void {
    if (this.state.foundAllWords || this.state.currentWord.length === 0) {
      return;
    }
    
    // Get the last added actual position (Sets maintain insertion order)
    const usedActualPositionsArray = Array.from(this.state.usedPositions);
    const lastUsedActualPosition = usedActualPositionsArray[usedActualPositionsArray.length - 1];
    
    if (lastUsedActualPosition !== undefined) {
      const newUsedPositions = new Set(this.state.usedPositions);
      newUsedPositions.delete(lastUsedActualPosition);
      
      this.setState({
        currentWord: this.state.currentWord.slice(0, -1),
        usedPositions: newUsedPositions,
      });
    }
  }

  // Toggle between randomized and original display order of characters
  // This only changes the visual presentation - the underlying usedPositions logic remains unchanged
  randomizeCharacters(): void {
    if (this.state.foundAllWords) {
      return;
    }

    if (this.state.isRandomized) {
      // Return to original order
      this.setState({
        displayOrder: Array.from({ length: this.props.dailyWord.length }, (_, i) => i),
        isRandomized: false,
      });
      
      // Log the return to normal order
      logClickRandomize(GameType.SUBTEXT, this.props.gameNumber, false);
    } else {
      // Apply the next deterministic shuffle
      const originalOrder = Array.from({ length: this.props.dailyWord.length }, (_, i) => i);
      const currentShuffleState = this.state.nextShuffleState;
      
      let shuffledOrder: number[];
      
      if (currentShuffleState === 1) {
        // First shuffle: reverse
        shuffledOrder = [...originalOrder].reverse();
      } else if (currentShuffleState === 2) {
        // Second shuffle: interleave
        shuffledOrder = this.interleaveShuffle(originalOrder);
      } else if (currentShuffleState === 3) {
        // Third shuffle: alphabetical with vowels/consonants split
        shuffledOrder = this.alphabeticalShuffle(originalOrder);
      } else {
        // Shouldn't happen, but fallback to original order
        shuffledOrder = [...originalOrder];
      }
      
      // Update only the display order - usedPositions and currentWord remain unchanged
      this.setState({
        displayOrder: shuffledOrder,
        isRandomized: true,
        nextShuffleState: (currentShuffleState % 3) + 1, // Cycle to next shuffle: 1->2->3->1
      });
      
      // Log the randomization
      logClickRandomize(GameType.SUBTEXT, this.props.gameNumber, true);
    }
  }

  // Interleave shuffle: alternate between first half and second half
  private interleaveShuffle(originalOrder: number[]): number[] {
    const mid = Math.ceil(originalOrder.length / 2);
    const firstHalf = originalOrder.slice(0, mid);
    const secondHalf = originalOrder.slice(mid);
    
    const result: number[] = [];
    const maxLength = Math.max(firstHalf.length, secondHalf.length);
    
    for (let i = 0; i < maxLength; i++) {
      if (i < firstHalf.length) {
        result.push(firstHalf[i]);
      }
      if (i < secondHalf.length) {
        result.push(secondHalf[i]);
      }
    }
    
    return result;
  }

  // Alphabetical shuffle: sort by character, with vowels and consonants separated
  private alphabeticalShuffle(originalOrder: number[]): number[] {
    const vowels = new Set(['a', 'e', 'i', 'o', 'u']);
    const dailyWord = this.props.dailyWord.toLowerCase();
    
    // Create array of {position, character} pairs
    const charPositions = originalOrder.map(pos => ({
      position: pos,
      character: dailyWord[pos]
    }));
    
    // Separate vowels and consonants
    const vowelPositions = charPositions.filter(item => vowels.has(item.character));
    const consonantPositions = charPositions.filter(item => !vowels.has(item.character));
    
    // Sort each group alphabetically
    vowelPositions.sort((a, b) => a.character.localeCompare(b.character));
    consonantPositions.sort((a, b) => a.character.localeCompare(b.character));
    
    // Combine: vowels first, then consonants
    return [...vowelPositions, ...consonantPositions].map(item => item.position);
  }

  // Submit current word
  submitWord(): void {
    if (this.state.foundAllWords) {
      return;
    }

    const currentWord = this.state.currentWord;

    if (currentWord.length === 0) {
      return;
    }

    if (currentWord.length < 4) {
      logInvalidWordAttempt(
        GameType.SUBTEXT,
        this.props.gameNumber,
        currentWord,
        'Too short',
        this.state.foundWords.length
      );
      this.showCustomErrorToast('Too short');
      return;
    }

    // Validate that the word is actually a subword (uses each character at most once)
    const dailyWordLower = this.props.dailyWord.toLowerCase();
    const currentWordLower = currentWord.toLowerCase();
    
    // Count characters in daily word
    const dailyWordCharCount: { [key: string]: number } = {};
    for (const char of dailyWordLower) {
      dailyWordCharCount[char] = (dailyWordCharCount[char] || 0) + 1;
    }
    
    // Count characters in current word
    const currentWordCharCount: { [key: string]: number } = {};
    for (const char of currentWordLower) {
      currentWordCharCount[char] = (currentWordCharCount[char] || 0) + 1;
    }
    
    // Check if current word uses more of any character than available in daily word
    for (const [char, count] of Object.entries(currentWordCharCount)) {
      if (!dailyWordCharCount[char] || count > dailyWordCharCount[char]) {
        logInvalidWordAttempt(
          GameType.SUBTEXT,
          this.props.gameNumber,
          currentWord,
          'Not a subword',
          this.state.foundWords.length
        );
        this.showCustomErrorToast('Not a subword');
        return;
      }
    }

    if (currentWord === this.props.dailyWord) {
      logInvalidWordAttempt(
        GameType.SUBTEXT,
        this.props.gameNumber,
        currentWord,
        'That would be too easy',
        this.state.foundWords.length
      );
      this.showCustomErrorToast('That would be too easy');
      return;
    }

    if (this.state.foundWords.includes(currentWord)) {
      logInvalidWordAttempt(
        GameType.SUBTEXT,
        this.props.gameNumber,
        currentWord,
        'Already found',
        this.state.foundWords.length
      );
      // Show different message and clear inputs for already found words
      this.showCustomErrorToast('Already found');
      return;
    }

    if (!this.props.validSubWords.includes(currentWord)) {
      logInvalidWordAttempt(
        GameType.SUBTEXT,
        this.props.gameNumber,
        currentWord,
        'Not in word list',
        this.state.foundWords.length
      );
      this.showCustomErrorToast('Not in word list');
      return;
    }

    const newFoundWords = [...this.state.foundWords, currentWord];
    const { totalPoints, bonusType } = getTotalPointsForWord(
      currentWord, 
      this.props.longestSubWordLength, 
      this.props.dailyWord.length
    );
    const newScore = this.state.score + totalPoints;
    
    // Check if max rank was just reached or if user just found all words
    const maxRankId = RANK_LEVELS.length - 1;
    const oldRank = getCurrentRank(this.state.score, this.props.realisticMaxScore);
    const newRank = getCurrentRank(newScore, this.props.realisticMaxScore);
    const justReachedMaxRank = oldRank.id !== maxRankId && newRank.id === maxRankId;
    const justFoundAllWords = newFoundWords.length >= this.props.validSubWords.length && this.state.foundWords.length < this.props.validSubWords.length;
    
    // Log successful word entry
    logWordEntered(
      GameType.SUBTEXT,
      this.props.gameNumber,
      currentWord,
      newFoundWords.length
    );
    
    // Log rank progression if reached new rank
    if (oldRank.id !== newRank.id) {
      logReachedNewRank(
        GameType.SUBTEXT,
        this.props.gameNumber,
        newRank.name
      );
    }
    
    // Log if all words were found
    if (justFoundAllWords) {
      logFoundAllWords(
        GameType.SUBTEXT,
        this.props.gameNumber,
        newFoundWords.length
      );
    }
    
    this.setState({
      foundWords: newFoundWords,
      score: newScore,
      currentWord: '',
      usedPositions: new Set(),
      foundAllWords: justFoundAllWords,
    });
    
    // Save updated words to localStorage
    setSubtextGameStats(this.props.gameNumber, newFoundWords);
    
    this.showCustomSuccessToast(this.getSuccessMessage(currentWord.length, bonusType), totalPoints, bonusType);
    
    // Open stats modal if max rank was just reached or all words were found
    if (justReachedMaxRank || justFoundAllWords) {
      // Delay opening the modal slightly to let the success toast show first
      setTimeout(() => {
        this.props.openGameStatsModal();
      }, 500);
    }
  }

  processKeyboardEvent(event: KeyboardEvent): void {
    // Ignore keyboard events when user is typing in an input field
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }
    const key = event.key.toLowerCase();
    
    // Handle letter input
    if (key.length === 1 && key >= 'a' && key <= 'z') {
      if (this.props.dailyWord.toLowerCase().includes(key)) {
        event.preventDefault();
        this.addCharacterByTyping(key);
      }
      return;
    }
    
    // Handle backspace/delete
    if (key === 'backspace' || key === 'delete') {
      event.preventDefault();
      this.removeCharacter();
      return;
    }
    
    // Handle enter
    if (key === 'enter') {
      event.preventDefault();
      this.submitWord();
      return;
    }
  }

  componentDidMount(): void {
    document.addEventListener('keydown', this.keyboardListener);
    
    // Load previously found words from localStorage
    const gameStats = getGameNumberStats(GameType.SUBTEXT, this.props.gameNumber) as SubtextGameStats;
    const savedWords = gameStats.words || [];
    
    if (savedWords.length > 0) {
      // Calculate score from saved words (including bonus points)
      const score = savedWords.reduce((total, word) => {
        const { totalPoints } = getTotalPointsForWord(
          word, 
          this.props.longestSubWordLength, 
          this.props.dailyWord.length
        );
        return total + totalPoints;
      }, 0);
      const hasFoundAllWords = savedWords.length >= this.props.validSubWords.length;
      
      this.setState({
        foundWords: savedWords,
        score: score,
        foundAllWords: hasFoundAllWords
      });
    }
    
    this.props.onGameLoad();
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.keyboardListener);
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  render(): React.ReactNode {
    return (
      <div className="game">
        <div className="subtext-game-container">
        {/* Top section: Progress + Words Found */}
        <div className="subtext-top-section">
          <SubtextProgressTracker 
            score={this.state.score} 
            realisticMaxScore={this.props.realisticMaxScore}
            onOpenRankingsModal={() => this.openRankingsModal()}
          />
          <SubtextWordsFoundBank 
            foundWords={this.state.foundWords} 
            onOpenModal={() => this.openWordsModal()} 
            longestSubWordLength={this.props.longestSubWordLength}
          />
        </div>

        {/* Center section: Current Word (flex grows) */}
        <div className="subtext-center-section">
          {this.state.successToasts.map(toast => (
            <React.Fragment key={toast.id}>
              <div className={`subtext-success-toast ${toast.bonusType ? `bonus-${toast.bonusType}` : ''}`}>
                {toast.message}
              </div>
              <div className={`subtext-points-toast ${toast.bonusType ? `bonus-${toast.bonusType}` : ''}`}>
                {toast.points}
              </div>
            </React.Fragment>
          ))}
          <SubtextCurrentWord 
            currentWord={this.state.currentWord} 
            isShaking={this.state.isShaking} 
            foundAllWords={this.state.foundAllWords && this.state.foundWords.length >= this.props.validSubWords.length}
          />
          {this.state.showErrorToast && (
            <div className="subtext-error-toast">
              {this.state.errorMessage}
            </div>
          )}
        </div>

        {/* Bottom section: Word Blocks + Controls */}
        <div className="subtext-bottom-section">
          <SubtextWordBlocks 
            dailyWord={this.props.dailyWord} 
            usedPositions={this.state.usedPositions}
            displayOrder={this.state.displayOrder}
            onCharacterClick={(char: string, actualPosition: number) => this.addCharacterByPosition(char, actualPosition)}
            disabled={this.state.foundAllWords}
            isRandomized={this.state.isRandomized}
          />
          <SubtextControls 
            onDelete={() => this.removeCharacter()}
            onEnter={() => this.submitWord()}
            onRandomize={() => this.randomizeCharacters()}
            isRandomized={this.state.isRandomized}
          />
        </div>

        {/* Words Modal */}
        <SubtextWordsModal
          foundWords={this.state.foundWords}
          isOpen={this.state.isWordsModalOpen}
          onClose={() => this.closeWordsModal()}
          longestSubWordLength={this.props.longestSubWordLength}
        />

        {/* Rankings Modal */}
        <SubtextRankingsModal
          score={this.state.score}
          realisticMaxScore={this.props.realisticMaxScore}
          isOpen={this.state.isRankingsModalOpen}
          onClose={() => this.closeRankingsModal()}
        />
        </div>
      </div>
    );
  }
}

// Export the component as default
export default SubtextGame; 